import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, MapPin } from "lucide-react";
import { SP } from "@/components/site/BrandLinks";
import MultilingualEditor from "@/components/ui/MultilingualEditor";
import { packMl, hasMlContent } from "@/lib/ml";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !hasMlContent(form.message)) {
      toast.error("Please share your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || null,
        message: packMl(form.message),
      });
      toast.success("Letter received. We'll reply within a few days.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check the form and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-32 md:pt-40">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16">
        <div className="overline text-[color:var(--ink-soft)] mb-6">§ Contact</div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease }}
          className="font-serif-display leading-[0.9] tracking-[-0.03em]"
          style={{ fontSize: "clamp(56px, 10vw, 180px)" }}
        >
          Write to <span className="font-italic-serif italic text-[color:var(--ink-soft)]">the archive.</span>
        </motion.h1>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24 grid grid-cols-12 gap-8 md:gap-12">
        <div className="col-span-12 md:col-span-5">
          <p className="text-[17px] leading-[1.6]">
            Corrections to a family record, cemetery submissions, partnership
            enquiries, or a story you'd like preserved — we read every letter
            and reply, usually within a week.
          </p>
          <div className="mt-10 space-y-6">
            <div>
              <div className="overline text-[color:var(--ink-soft)] mb-2">Correspondence</div>
              <a
                href="mailto:archive@myfamilytree.care"
                className="font-serif-display link-underline"
                style={{ fontSize: "clamp(22px, 2.4vw, 32px)" }}
                data-testid="contact-email-link"
              >
                archive@myfamilytree.care
              </a>
            </div>
            <div>
              <div className="overline text-[color:var(--ink-soft)] mb-2">Editorial desk</div>
              <a
                href="mailto:editors@myfamilytree.care"
                className="font-serif-display link-underline"
                style={{ fontSize: "clamp(22px, 2.4vw, 32px)" }}
              >
                editors@myfamilytree.care
              </a>
            </div>
            <div>
              <div className="overline text-[color:var(--ink-soft)] mb-2">Postal</div>
              <div className="text-[15px] leading-[1.6] text-[color:var(--ink-soft)]">
                FamilyTree.Team<br />
                c/o <SP /> — Heritage Desk<br />
                Care of the reader,<br />
                Wherever this reaches you.
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-7">
          <form
            onSubmit={submit}
            className="border border-[color:var(--ink-line-strong)] p-6 md:p-10 bg-[color:var(--bone)]"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 md:col-span-1">
                <label className="overline text-[color:var(--ink-soft)] block mb-2">Your name</label>
                <input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-[color:var(--ink-line-strong)] pb-2 focus:outline-none focus:border-[color:var(--ink)] text-[16px]"
                  maxLength={80}
                  required
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="overline text-[color:var(--ink-soft)] block mb-2">Email</label>
                <input
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-transparent border-b border-[color:var(--ink-line-strong)] pb-2 focus:outline-none focus:border-[color:var(--ink)] text-[16px]"
                  required
                />
              </div>
              <div className="col-span-2">
                <label className="overline text-[color:var(--ink-soft)] block mb-2">Subject</label>
                <input
                  data-testid="contact-subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="w-full bg-transparent border-b border-[color:var(--ink-line-strong)] pb-2 focus:outline-none focus:border-[color:var(--ink)] text-[16px]"
                  maxLength={140}
                  placeholder="Correction · Cemetery · Partnership · Other"
                />
              </div>
              <div className="col-span-2">
                <label className="overline text-[color:var(--ink-soft)] block mb-2">Your letter</label>
                <MultilingualEditor
                  as="textarea"
                  rows={6}
                  testid="contact-message"
                  value={form.message}
                  onChange={(v) => setForm({ ...form, message: v })}
                  maxLength={2000}
                />
              </div>
              <div className="col-span-2 flex items-center justify-between mt-2">
                <span className="numeric-index text-[color:var(--ink-soft)]">
                  We reply personally — usually within a week.
                </span>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-rust disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {loading ? "Sending…" : "Send letter"} <span aria-hidden>→</span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}
